import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Eye, Phone, MapPin, Sparkles, Loader2, FileDown, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function YouthList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [villages, setVillages] = useState([]);
  const [village, setVillage] = useState("all");
  const [mandal, setMandal] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRec, setAiRec] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/youth/${toDelete.youth_id}`);
      toast.success(`Removed ${data.registration_no}`);
      setList((p) => p.filter((y) => y.youth_id !== toDelete.youth_id));
      setToDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const downloadExport = async (kind) => {
    const setLoad = kind === "pdf" ? setPdfLoading : setXlsxLoading;
    setLoad(true);
    try {
      const params = [];
      if (village && village !== "all") params.push(`village=${encodeURIComponent(village)}`);
      if (mandal && mandal !== "all") params.push(`mandal=${encodeURIComponent(mandal)}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const res = await fetch(`${API}/youth-export/${kind}${qs}`, {
        credentials: "include",
        headers: localStorage.getItem("psi_token")
          ? { Authorization: `Bearer ${localStorage.getItem("psi_token")}` }
          : {},
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const scope = mandal !== "all" ? `_${mandal}` : village !== "all" ? `_${village}` : "";
      a.href = url;
      a.download = `padaskillinfo_registrations${scope}_${new Date().toISOString().split("T")[0]}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded (${filtered.length} record(s))`);
    } catch (e) {
      toast.error(`${kind.toUpperCase()} download failed`);
    } finally {
      setLoad(false);
    }
  };

  const downloadPdf = () => downloadExport("pdf");
  const downloadXlsx = () => downloadExport("xlsx");

  const openDetails = (y) => {
    setOpen(y);
    setAiRec(y.ai_recommendation || null);
  };

  const fetchRecommendation = async () => {
    if (!open) return;
    setAiLoading(true);
    try {
      const force = aiRec ? "?force=true" : "";
      const { data } = await api.post(`/youth/${open.youth_id}/recommend${force}`);
      setAiRec(data);
      toast.success("AI recommendation ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setAiLoading(false);
    }
  };

  const load = (v, m) => {
    setLoading(true);
    const params = [];
    if (v && v !== "all") params.push(`village=${encodeURIComponent(v)}`);
    if (m && m !== "all") params.push(`mandal=${encodeURIComponent(m)}`);
    params.push("limit=10000");
    api.get(`/youth?${params.join("&")}`)
      .then(({ data }) => setList(data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/villages").then(({ data }) => setVillages(data));
    load(village, mandal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(village, mandal); /* eslint-disable-next-line */ }, [village, mandal]);

  const filtered = useMemo(() => list.filter((y) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      y.full_name?.toLowerCase().includes(s) ||
      y.mobile?.includes(s) ||
      y.registration_no?.toLowerCase().includes(s) ||
      y.aadhaar_number?.includes(s)
    );
  }), [list, q]);

  const villageOptions = useMemo(
    () => villages.filter((v) => mandal === "all" || v.mandal === mandal),
    [villages, mandal]
  );

  return (
    <>
    <div className="p-6 lg:p-8" data-testid="youth-list-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl">Youth Registrations</h1>
          <p className="text-gray-600 text-sm">
            <span className="font-heading font-bold text-[#1E3A8A] text-base">{filtered.length}</span> record(s)
            {mandal !== "all" && <> · <span className="font-medium">{mandal}</span> mandal</>}
            {village !== "all" && <> · <span className="font-medium">{village}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input className="pl-9 w-64" placeholder="Name / mobile / reg-no" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
          </div>
          <Select value={mandal} onValueChange={(v) => { setMandal(v); setVillage("all"); }}>
            <SelectTrigger className="w-44" data-testid="filter-mandal"><SelectValue placeholder="Mandal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Mandals</SelectItem>
              <SelectItem value="Pithapuram">Pithapuram</SelectItem>
              <SelectItem value="Gollaprolu">Gollaprolu</SelectItem>
              <SelectItem value="Kothapalle">Kothapalle</SelectItem>
            </SelectContent>
          </Select>
          <Select value={village} onValueChange={setVillage}>
            <SelectTrigger className="w-48" data-testid="filter-village"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Villages</SelectItem>
              {villageOptions.map((v) => <SelectItem key={v.village_id} value={v.name}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={downloadPdf}
            disabled={pdfLoading || filtered.length === 0}
            className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white"
            data-testid="download-pdf-btn"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            PDF
          </Button>
          <Button
            onClick={downloadXlsx}
            disabled={xlsxLoading || filtered.length === 0}
            className="bg-[#10B981] hover:bg-[#10B981]/90 text-white"
            data-testid="download-xlsx-btn"
          >
            {xlsxLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Excel
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
        {loading ? (
          <div className="p-10 text-gray-500 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-gray-500 text-center">
            No registrations yet. Share the registration link with your team to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr className="text-xs uppercase tracking-wider text-gray-600">
                  <th className="p-3">Reg No.</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Village</th>
                  <th className="p-3">Education</th>
                  <th className="p-3">Looking for</th>
                  <th className="p-3">Tags</th>
                  <th className="p-3">Date</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((y) => (
                  <tr key={y.youth_id} className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="p-3 font-mono text-xs">{y.registration_no}</td>
                    <td className="p-3">
                      <div className="font-medium">{y.full_name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {y.mobile}</div>
                    </td>
                    <td className="p-3"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-[#E11D48]" /> {y.village}</span></td>
                    <td className="p-3">{y.education_level}<div className="text-xs text-gray-500">{y.education_status}</div></td>
                    <td className="p-3">{y.placement_preference}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {y.ai_upskill_interest && <Badge className="bg-[#F59E0B] hover:bg-[#F59E0B]">AI</Badge>}
                        {y.bank_linkage_required && <Badge className="bg-[#10B981] hover:bg-[#10B981]">Bank</Badge>}
                        {y.startup_idea && <Badge className="bg-[#E11D48] hover:bg-[#E11D48]">Startup</Badge>}
                      </div>
                    </td>
                    <td className="p-3 text-xs text-gray-500">{new Date(y.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openDetails(y)} data-testid={`view-${y.youth_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setToDelete(y)}
                            className="text-[#E11D48] hover:text-[#E11D48] hover:bg-[#E11D48]/10"
                            data-testid={`delete-${y.youth_id}`}
                            title="Delete registration"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{open?.full_name}</DialogTitle>
            <p className="text-xs text-gray-500 font-mono">{open?.registration_no}</p>
          </DialogHeader>
          {open && (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Row k="Aadhaar" v={open.aadhaar_number} />
              <Row k="Gender" v={open.gender} />
              <Row k="DOB" v={open.date_of_birth} />
              <Row k="Mobile" v={open.mobile} />
              <Row k="Email" v={open.email} />
              <Row k="Village" v={open.village} />
              <Row k="Pincode" v={open.pincode} />
              <Row k="Address" v={open.address} />
              <Row k="Education" v={`${open.education_level} (${open.education_status})`} />
              <Row k="Qualification" v={open.qualification_details} />
              <Row k="Employment" v={open.employment_status} />
              <Row k="Family Income" v={open.family_income} />
              <Row k="Looking for" v={open.placement_preference} />
              <Row k="Job Type" v={open.job_type_preference} />
              <Row k="Domains" v={open.interested_domains?.join(", ")} />
              <Row k="Handicrafts" v={open.interested_handicrafts?.join(", ")} />
              <Row k="Self-employment trades" v={open.self_employment_interests?.join(", ")} />
              <Row k="Skills" v={open.skills_known?.join(", ")} />
              <Row k="Languages" v={open.languages_known?.join(", ")} />
              <Row k="AI Upskill" v={open.ai_upskill_interest ? "Yes" : "No"} />
              <Row k="Bank Linkage" v={open.bank_linkage_required ? `Yes - ${open.bank_loan_amount || "-"}` : "No"} />
              <div className="sm:col-span-2">
                <div className="text-gray-500 text-xs mb-1">Existing skill / craft already practised</div>
                <div className="bg-gray-50 p-3 rounded-md whitespace-pre-wrap">{open.existing_skill_details || "—"}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-gray-500 text-xs mb-1">Desired skill upgrade & experience</div>
                <div className="bg-gray-50 p-3 rounded-md whitespace-pre-wrap">{open.desired_upskill_with_experience || "—"}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-gray-500 text-xs mb-1">Startup Idea</div>
                <div className="bg-gray-50 p-3 rounded-md">{open.startup_idea || "—"}</div>
              </div>

              {/* AI Recommendation */}
              <div className="sm:col-span-2 mt-2 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#F59E0B]" />
                    <h4 className="font-heading font-bold">AI Track Recommendation</h4>
                  </div>
                  <Button size="sm" onClick={fetchRecommendation} disabled={aiLoading} className="bg-gradient-to-r from-[#1E3A8A] to-[#E11D48] hover:opacity-90 text-white" data-testid="ai-recommend-btn">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {aiRec ? "Regenerate" : "Get AI Recommendation"}
                  </Button>
                </div>
                {aiRec ? (
                  <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FFE4E6] rounded-xl p-5 border border-[#FCD34D]/40" data-testid="ai-rec-card">
                    <div className="flex items-baseline gap-3 mb-3">
                      <div className="font-heading font-bold text-2xl text-[#1E3A8A]">{aiRec.recommended_track}</div>
                      {aiRec.track_duration_weeks && (
                        <Badge className="bg-[#1E3A8A] hover:bg-[#1E3A8A]">{aiRec.track_duration_weeks} weeks</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{aiRec.why}</p>
                    {aiRec.steps?.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wider text-[#92400E] font-semibold mb-1">Action steps</div>
                        <ol className="text-sm list-decimal list-inside space-y-1">
                          {aiRec.steps.map((s, i) => <li key={`step-${i}`}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
                      {aiRec.job_or_startup && (
                        <div><div className="text-xs text-gray-500">Best path</div><div className="font-medium">{aiRec.job_or_startup}</div></div>
                      )}
                      {aiRec.bank_scheme && (
                        <div><div className="text-xs text-gray-500">Bank scheme</div><div className="font-medium">{aiRec.bank_scheme}</div></div>
                      )}
                    </div>
                    {aiRec.placement_options?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-gray-500">Placement options</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {aiRec.placement_options.map((p) => <Badge key={`po-${p}`} variant="outline">{p}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-md p-3">
                    Click the button above to get an AI-powered career recommendation tailored to this youth.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2 text-[#E11D48]">
              <Trash2 className="w-5 h-5" /> Delete registration?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You're about to permanently delete{" "}
                <span className="font-mono font-semibold">{toDelete?.registration_no}</span> —{" "}
                <span className="font-semibold">{toDelete?.full_name}</span>{" "}
                ({toDelete?.village}).
              </span>
              <span className="block text-[#E11D48] text-xs font-medium">
                This cannot be undone. The youth's profile, AI recommendation and selected courses will be removed.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#E11D48] hover:bg-[#E11D48]/90"
              data-testid="confirm-delete"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Row({ k, v }) {
  return (
    <div>
      <div className="text-gray-500 text-xs">{k}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}
